/**
 * DocumentTabContentTipTap
 *
 * Routes document rendering based on document type.
 * Similar to DocumentTabContent but uses TipTapDocumentPane for tiptap documents.
 */

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDocumentContext } from "./DocumentAgentLGContext";
import { DocumentSnapshot, PDFDocument } from "./DocumentAgentLGTypes";
import TipTapDocumentPane from "./TipTapDocumentPane";
import DocumentAgentPDF from "../DocumentAgent/DocumentAgentPDF";
import { fetchPdf } from "../utils";
import { Button, Form } from "react-bootstrap";

interface DocumentTabContentTipTapProps {
  workproductId: string;
  workproductType: string;
  docId: string;
  documentName?: string;
  onNewDocument?: () => void;
  onImportDocxAsNewTab?: (file: File) => Promise<void>;
  onImportPdfAsNewTab?: (file: File) => Promise<void>;
}

const DocumentTabContentTipTap: React.FC<DocumentTabContentTipTapProps> = ({
  workproductId,
  workproductType,
  docId,
  documentName,
  onNewDocument,
  onImportDocxAsNewTab,
  onImportPdfAsNewTab,
}) => {
  const { t } = useTranslation();
  const { getDocumentById } = useDocumentContext();
  const [pdfUrl, setPdfUrl] = useState<string>("");
  const [isLoadingPdf, setIsLoadingPdf] = useState<boolean>(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  const doc = getDocumentById(docId) as DocumentSnapshot | undefined;

  // PDF loading logic
  useEffect(() => {
    const loadPdf = async () => {
      if (!doc || doc.documentType !== "pdf") return;
      try {
        setIsLoadingPdf(true);
        setPdfError(null);
        const pdfDoc = doc as PDFDocument;

        // If we have a pre-signed URL (from shared view), use it directly
        if (pdfDoc.signedPdfUrl) {
          setPdfUrl(pdfDoc.signedPdfUrl);
          return;
        }

        // Otherwise, fetch a signed URL via the authenticated API
        const candidatePath = pdfDoc.pdfGcsPath;
        if (!candidatePath) {
          throw new Error("No pdfGcsPath found in document metadata");
        }
        const url = await fetchPdf(workproductId, candidatePath);
        if (!url) throw new Error("Failed to get signed URL for PDF");
        setPdfUrl(url);
      } catch (e: any) {
        setPdfError(e?.message || "Failed to load PDF");
      } finally {
        setIsLoadingPdf(false);
      }
    };

    if (doc?.documentType === "pdf") {
      loadPdf();
    }
  }, [doc, workproductId, docId]);

  // Document might not be available yet (e.g., just uploaded, context not yet updated)
  // Show a loading state instead of "not found" to handle this race condition
  if (!doc) {
    return (
      <div className="tiptap-document-tab-content">
        <div style={{ padding: "20px", textAlign: "center" }}>
          <div
            className="tiptap-upload-spinner"
            style={{ width: 32, height: 32, margin: "0 auto 12px" }}
          />
          <div>
            {t("DocumentTabContent.loadingDocument", "Loading document...")}
          </div>
        </div>
      </div>
    );
  }

  // TipTap document
  if (doc.documentType === "tiptap") {
    return (
      <TipTapDocumentPane
        key={doc.id}
        workproductId={workproductId}
        workproductType={workproductType}
        docId={doc.id}
        documentName={doc.documentName}
        onNewDocument={onNewDocument}
        onImportDocxAsNewTab={onImportDocxAsNewTab}
        onImportPdfAsNewTab={onImportPdfAsNewTab}
      />
    );
  }

  // PDF document
  if (doc.documentType === "pdf") {
    if (isLoadingPdf) {
      return (
        <div className="tiptap-document-tab-content">
          <div style={{ padding: "20px", textAlign: "center" }}>
            {t("DocumentTabContent.loadingPDF", "Loading PDF...")}
          </div>
        </div>
      );
    }
    if (pdfError || !pdfUrl) {
      return (
        <div className="tiptap-document-tab-content">
          <div style={{ padding: "20px", textAlign: "center" }}>
            <p>
              {t("DocumentTabContent.pdfNotAvailable", {
                documentName: doc.documentName,
                defaultValue: `PDF "${doc.documentName}" is not available`,
              })}
            </p>
            <InlinePdfUpload
              workproductId={workproductId}
              currentDoc={doc as PDFDocument}
            />
          </div>
        </div>
      );
    }
    return (
      <DocumentAgentPDF
        pdfUrls={[pdfUrl]}
        documentName={doc.documentName || documentName}
        showControls={true}
        showTabs={false}
        style={{ height: "calc(100vh - 100px)" }}
      />
    );
  }

  return (
    <div className="tiptap-document-tab-content">
      <div style={{ padding: "20px", textAlign: "center" }}>
        This document format is not supported.
      </div>
    </div>
  );
};

export default DocumentTabContentTipTap;

// ============================================================================
// INLINE PDF UPLOAD (fallback when PDF is missing)
// ============================================================================

const InlinePdfUpload: React.FC<{
  workproductId: string;
  currentDoc: PDFDocument;
}> = ({ workproductId, currentDoc }) => {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { addPDFDocument } = useDocumentContext();

  const backendUrl =
    process.env.REACT_APP_BACKEND_URL || "http://localhost:3001";

  const onUpload = async () => {
    if (!file) return;
    setIsUploading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("documentName", currentDoc.documentName || "Patent");
      formData.append("workproductId", workproductId);
      formData.append("docId", currentDoc.id);

      const resp = await fetch(`${backendUrl}/api/document-agent/upload-pdf`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(text || `Upload failed with status ${resp.status}`);
      }
      const result = await resp.json();
      addPDFDocument(
        result.docId,
        result.documentName,
        result.pdfGcsPath,
        result.ocrGcsPath,
        result.ocrMetadata
      );
    } catch (e: any) {
      setError(e?.message || "Failed to upload PDF");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div style={{ marginTop: 16 }}>
      <Form.Control
        type="file"
        accept="application/pdf"
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setFile(e.currentTarget.files?.[0] || null)
        }
        disabled={isUploading}
      />
      <div style={{ marginTop: 8 }}>
        <Button onClick={onUpload} disabled={!file || isUploading}>
          {isUploading
            ? t("DocumentTabContent.uploading", {
                documentName: currentDoc.documentName,
              })
            : t("DocumentTabContent.upload", {
                documentName: currentDoc.documentName,
              })}
        </Button>
      </div>
      {error && <div style={{ marginTop: 8, color: "#b00020" }}>{error}</div>}
    </div>
  );
};
